import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useGuideMe from 'src/hooks/useGuideMe';
import { useContextSelector } from 'use-context-selector';
import useGuideMeSteps from './GuideMeSteps';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

const ViewShoppingListsHeader = () => {
  const { t } = useTranslation();
  const searchValue = useContextSelector(ViewShoppingListsContext, (ctx) => ctx.searchValue);
  const setSearchValue = useContextSelector(ViewShoppingListsContext, (ctx) => ctx.setSearchValue);
  const canExportSelectedShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (ctx) => ctx.canExportSelectedShoppingLists
  );
  const exportSelectedShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (ctx) => ctx.exportSelectedShoppingLists
  );
  const isExportingShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (ctx) => ctx.isExportingShoppingLists
  );
  const guideMeSteps = useGuideMeSteps();
  const { guideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'view-shopping-lists-guide-me',
  });

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
  }, [setSearchValue]);

  return (
    <div className="mb-2 mt-4 flex w-full flex-wrap items-center justify-between gap-4 px-4">
      <div className="flex items-center gap-2 flex-[1_1_350px] min-w-[280px] max-w-[550px]">
        <FieldWrapper className="flex flex-row items-center space-x-2 w-full">
          <Label
            editorId="view-shopping-lists-filter-input"
            className="text-xs flex items-center whitespace-nowrap font-semibold"
          >
            {t('Filter By')}
          </Label>
          <TextBox
            id="view-shopping-lists-filter-input"
            max={500}
            maxLength={500}
            size="medium"
            style={{ fontSize: '14px' }}
            placeholder={t('List Name or Owner Name')}
            width="100%"
            value={searchValue}
            onChange={(event) => setSearchValue(String(event.value ?? ''))}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            prefix={() => (
              <InputPrefix className="pl-2">
                <Icon height="18" width="18" color="#AAAAAA" icon="lucide:search" />
              </InputPrefix>
            )}
            suffix={() => (
              <InputSuffix className="pr-2">
                {searchValue && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    aria-label={t('clearSearch')}
                    className="flex items-center justify-center"
                  >
                    <Icon height="16" width="16" color="#AAAAAA" icon="lucide:x" />
                  </button>
                )}
              </InputSuffix>
            )}
          />
        </FieldWrapper>
      </div>
      <div className="flex items-center gap-1 ml-auto flex-shrink-0">
        <PopUp
          popupAlign={{ horizontal: 'center', vertical: 'top' }}
          anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
          className="max-h-[180px]"
          content={
            <div className="m-1 max-w-[350px]">
              <PopUpContentWrapper
                type="Information"
                contents={[t('Please select list(s) to export.')]}
              />
            </div>
          }
          show={!canExportSelectedShoppingLists}
        >
          <Button
            color="secondary"
            disabled={!canExportSelectedShoppingLists}
            loading={isExportingShoppingLists}
            onClick={() => {
              void exportSelectedShoppingLists();
            }}
          >
            {t('exportToExcel')}
          </Button>
        </PopUp>
        {guideMe}
      </div>
    </div>
  );
};

export default ViewShoppingListsHeader;
